import {
  Box,
  Divider,
  FormHelperText,
  MenuItem,
  RadioGroup,
  Select,
  Switch,
  TextField,
  Typography,
  capitalize,
} from '@mui/material';
import {
  AthletePreview,
  BlockPrintSettings,
  PrintEachSidePlacement,
  PrintLettering,
  PrintOrientation,
  PrintView,
  PrintWorkoutInstructions,
  PrintWorkoutLayout,
  PrintHeaderSize,
  athleteDisplayName,
  printOptionDefaults,
} from '@strength/domain';
import {
  findPrintedExercise,
  getExerciseDisplay,
  mergeWorkoutLayers,
  PrintedExerciseTarget as PrintedExerciseTargetBase,
  resolvePrintedExerciseTarget,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import {
  FormEvent,
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '../../../../loco-sync';
import { trackEvent } from '../../../../reporting/reporting';
import { FormNotification } from '../../../components/form/form-notification';
import { InputLabel } from '../../../components/input/input-label';
import { LabeledRadioChip } from '../../../components/input/labeled-radio-chip';
import { MultiSelect } from '../../../components/input/multi-select';
import { useFlags } from '../../../context/featureFlag';
import { useViewer } from '../../../context/viewer';
import { toast } from '../../../hooks/useToast';
import { getLocale } from '../../../utils/i18n';
import {
  AthleteDraftWorkoutLayer,
  BlockDraftWorkoutLayer,
  BlockPreview,
} from '../types';
import { ComparableWorkout, compareWorkouts } from './compare-workouts';
import { useGenerateWorkoutPDF } from './useGeneratePDF';
import { WorkoutSelector } from './WorkoutSelector';
import { sortByOrder } from '@strength/utils';
import { useMetrics } from '../../../hooks/useMetrics';

interface PrintTabProps {
  blockWorkoutLayers: BlockDraftWorkoutLayer[];
  athletes: AthletePreview[];
  block: BlockPreview;
  blockPrintSettings: BlockPrintSettings;
  initCycleId: number | undefined;
  initSessionId: number | undefined;
  initAthleteId: string | null;
  onDisabledChange: (disabled: boolean) => void;
}

const printDefaults = printOptionDefaults();

// The Export drawer's footer lives outside this tab (it's shared chrome that
// also serves the Digital Display tab), so the Print submit button in
// ExportDrawer.tsx targets this form by id rather than living in here.
export const PRINT_TAB_FORM_ID = 'print-tab-form';

export const PrintTab = ({
  athletes,
  block,
  initAthleteId,
  initCycleId,
  initSessionId,
  blockWorkoutLayers,
  blockPrintSettings,
  onDisabledChange,
}: PrintTabProps) => {
  const { t } = useTranslation();
  const [sessionIds, setSessionIds] = useState<number[]>(
    initSessionId ? [initSessionId] : [],
  );
  const [cycleIds, setCycleIds] = useState<number[]>(
    initCycleId ? [initCycleId] : [],
  );
  const [exerciseId, setExerciseId] = useState<string | null>(null);
  const [metricIds, setMetricIds] = useState<string[]>([]);
  const [athleteIds, setAthleteIds] = useState<string[]>(
    initAthleteId ? [initAthleteId] : [],
  );
  const [sets, setSets] = useState<number[] | null>(null);

  const [layout, setLayout] = useState<PrintWorkoutLayout>('workout');
  const [orientation, setOrientation] = useState<PrintOrientation>(
    printDefaults.orientation,
  );
  const [view, setView] = useState<PrintView>(printDefaults.view);
  const [instructions, setInstructions] = useState<PrintWorkoutInstructions>(
    printDefaults.instructions,
  );
  const [lettering, setLettering] = useState<PrintLettering>(
    printDefaults.lettering,
  );
  const [fitToPage, setFitToPage] = useState<boolean>(printDefaults.fitToPage);
  const [eachSidePlacement, setEachSidePlacement] =
    useState<PrintEachSidePlacement>(printDefaults.eachSidePlacement);
  const { data: athleteWorkoutLayers } = useQuery('AthleteDraftWorkoutLayer', {
    blockId: block.id,
  });
  const [printBaseBlock, setPrintBaseBlock] = useState<boolean>(false);
  const [headerText, setHeaderText] = useState<string | null>(null);
  const [headerTextTooLong, setHeaderTextTooLong] = useState<boolean>(false);
  const [headerSize, setHeaderSize] = useState(printDefaults.headerSize);
  const headerCharLimit = useMemo(
    () => (headerSize === 'large' ? 200 : 100),
    [headerSize],
  );

  useEffect(() => {
    setHeaderTextTooLong((headerText?.length ?? 0) > headerCharLimit);
    // This is just to reset the error styling if the header size is toggled
    // we don't include the headerText in the deps because that's already handled
    // by the text area
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerCharLimit]);

  const { generateWorkoutPDF, pdfGenerating } = useGenerateWorkoutPDF({
    blockId: block.id,
    onComplete: (url) => {
      window.open(url, '_blank');
    },
  });
  const { navbar } = useViewer();
  const { selectedBlockLayers, selectedAthleteLayers } = useMemo(
    () =>
      getWorkoutsForLayoutAndFilters(
        layout,
        blockWorkoutLayers,
        athleteWorkoutLayers.filter((a) => athleteIds.includes(a.profileId)),
        sessionIds,
        cycleIds,
      ),
    [
      layout,
      blockWorkoutLayers,
      athleteWorkoutLayers,
      sessionIds,
      cycleIds,
      athleteIds,
    ],
  );

  const blockUniformityResults = useMemo(
    () =>
      layout !== 'uniform-session'
        ? []
        : validateBlockUniformity(
            block,
            athleteIds,
            selectedBlockLayers,
            selectedAthleteLayers,
            t,
            printBaseBlock,
          ),
    [
      layout,
      block,
      athleteIds,
      selectedBlockLayers,
      selectedAthleteLayers,
      t,
      printBaseBlock,
    ],
  );

  // Exercise layout matches the picked exercise in each athlete's resolved
  // workout, so list the athletes it is missing for. Warn only: the print still
  // works, those athletes are just absent from the card.
  const missingExerciseAthletes = useMemo(() => {
    if (layout !== 'exercise') return null;

    const blockLayer = blockWorkoutLayers.find(
      (w) =>
        cycleIds.includes(w.cycleSortOrder) &&
        sessionIds.includes(w.sessionSortOrder),
    );
    const target = resolvePrintedExercise(blockLayer, exerciseId);
    if (!blockLayer || !target) return null;

    const missingAthleteIds = findAthletesMissingExercise(
      athleteIds,
      blockLayer,
      athleteWorkoutLayers.filter((a) => athleteIds.includes(a.profileId)),
      target,
    );
    return missingAthleteIds.length ? { target, missingAthleteIds } : null;
  }, [
    layout,
    blockWorkoutLayers,
    cycleIds,
    sessionIds,
    exerciseId,
    athleteIds,
    athleteWorkoutLayers,
  ]);

  const disabled =
    (!athleteIds.length && !printBaseBlock) ||
    (!sessionIds.length && !cycleIds.length) ||
    pdfGenerating ||
    headerTextTooLong ||
    blockUniformityResults.length > 0 ||
    !isValidFiltersForLayout(
      layout,
      sessionIds,
      cycleIds,
      exerciseId,
      metricIds,
    );

  const workoutIds = useMemo(
    () => resolvePrintJobWorkouts(selectedBlockLayers, selectedAthleteLayers),
    [selectedBlockLayers, selectedAthleteLayers],
  );

  const submitPrintJob = () => {
    trackEvent('Strength | Block Builder | Print Workout', {
      blockId: block.id,
      layout,
      orientation,
      view,
      lettering,
      workoutCount: workoutIds.length,
      setCount: sets?.length ?? 0,
    });
    generateWorkoutPDF({
      profileIds: athleteIds,
      workoutIds,
      printOptions: {
        layout,
        orientation,
        view,
        instructions,
        exerciseId,
        metricIds,
        sets,
        brandColor: navbar.navbarBottomColor ?? null,
        locale: getLocale(),
        lettering,
        fitToPage,
        printBaseBlock,
        headerText: headerText?.trim() || null,
        headerSize,
        eachSidePlacement,
      },
    });
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!workoutIds || workoutIds.length === 0) {
      toast({
        title: t('noWorkoutsFoundForSelection'),
        severity: 'error',
      });
      return;
    }

    submitPrintJob();
  };

  // The submit button for this form lives in ExportDrawer.tsx's shared
  // footer, so its `disabled` state has to be reported upward. useLayoutEffect
  // (rather than useEffect) keeps the footer button from ever painting a
  // stale disabled state for a frame after this tab mounts or its fields change.
  useLayoutEffect(() => {
    onDisabledChange(disabled);
  }, [disabled, onDisabledChange]);

  // Keeps Enter from submitting, which is what it did before these fields were
  // wrapped in a <form>. The footer's Print button is this form's default
  // button (it targets PRINT_TAB_FORM_ID from outside), so without this an
  // Enter press in a MultiSelect's text input queues a print task and later
  // opens the finished PDF in a new tab - from a keystroke the coach meant for
  // the open option list. Deliberately not applied to the Digital Display tab,
  // where Enter saves a validated, reversible selection.
  const suppressImplicitSubmit = (e: KeyboardEvent<HTMLFormElement>) => {
    if (e.key !== 'Enter' || e.defaultPrevented) return;
    // MultiSelect's Autocomplete sits deeper, so it has already handled Enter
    // (and marked it) whenever an option is highlighted - that selection must
    // still work. Limited to <input> so the multiline header text, where Enter
    // inserts a newline, is untouched.
    if ((e.target as HTMLElement).tagName !== 'INPUT') return;
    e.preventDefault();
  };

  return (
    <form
      id={PRINT_TAB_FORM_ID}
      onSubmit={onSubmit}
      onKeyDown={suppressImplicitSubmit}
      className="flex flex-col p-1 px-8 grow gap-4 pb-6"
    >
      <TextField
        select
        label={t('cardLayout')}
        value={layout}
        onChange={(e) => {
          const value = e.target.value as PrintWorkoutLayout;
          if (!value) return;
          setLayout(value);
          setExerciseId(printDefaults.exerciseId);
          setMetricIds(printDefaults.metricIds);
          setOrientation(printDefaults.orientation);
          setView(printDefaults.view);
          setInstructions(printDefaults.instructions);
          setSets(printDefaults.sets);
          setEachSidePlacement(printDefaults.eachSidePlacement);

          // Reset the cycle and session ids to defaults
          if (value === 'workout') {
            setCycleIds(initCycleId ? [initCycleId] : []);
            setSessionIds(initSessionId ? [initSessionId] : []);
          } else if (value === 'cycle') {
            setCycleIds(initCycleId ? [initCycleId] : []);
            setSessionIds([]);
          } else if (value === 'session' || value === 'uniform-session') {
            setSessionIds(initSessionId ? [initSessionId] : []);
            setCycleIds([]);
          } else if (value === 'exercise') {
            setCycleIds(initCycleId ? [initCycleId] : []);
            setSessionIds(initSessionId ? [initSessionId] : []);
            setPrintBaseBlock(false);
          }
        }}
        sx={{ width: '100%' }}
      >
        <MenuItem value="workout">{t('workout')}</MenuItem>
        <MenuItem value="cycle">
          {capitalize(blockPrintSettings.cycleLabel)}
        </MenuItem>
        <MenuItem value="session">
          {capitalize(blockPrintSettings.sessionLabel)}
        </MenuItem>
        <MenuItem value="uniform-session">
          {t('uniformSession', {
            sessionLabel: capitalize(blockPrintSettings.sessionLabel),
          })}
        </MenuItem>
        <MenuItem value="exercise">{t('exercise')}</MenuItem>
      </TextField>

      {blockUniformityResults.length > 0 && (
        <FormNotification
          title={t('blockIsNotUniform')}
          type="warning"
          className="mt-2 max-w-[400px]"
        >
          <div>{t('blockIsNotUniformMessage')}</div>
          {blockUniformityResults.map(({ athleteId, results }) => {
            const athlete = athletes.find((a) => a.id === athleteId);
            const errors = Object.values(results).flat();
            return (
              <div className="mt-2">
                <span className="font-semibold">
                  {athlete ? athleteDisplayName(athlete) : t('baseBlock')}
                </span>
                <ul className="mt-0 list-disc">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </FormNotification>
      )}

      {missingExerciseAthletes && (
        <FormNotification
          title={t('missingExercise')}
          type="warning"
          className="mt-2 max-w-[400px]"
        >
          <div>
            {t('missingExerciseWarningMessage', {
              exerciseName: missingExerciseAthletes.target.exerciseName,
              sectionName: missingExerciseAthletes.target.sectionName,
            })}
          </div>
          <ul className="mt-0 list-disc">
            {missingExerciseAthletes.missingAthleteIds.map((athleteId) => {
              const athlete = athletes.find((a) => a.id === athleteId);
              return (
                <li key={athleteId}>
                  {athlete ? athleteDisplayName(athlete) : ''}
                </li>
              );
            })}
          </ul>
        </FormNotification>
      )}

      <PrintSelect
        layout={layout}
        block={block}
        blockPrintSettings={blockPrintSettings}
        athletes={athletes}
        athleteIds={athleteIds}
        setAthleteIds={setAthleteIds}
        cycleIds={cycleIds}
        setCycleIds={setCycleIds}
        sessionIds={sessionIds}
        setSessionIds={setSessionIds}
        exerciseId={exerciseId}
        setExerciseId={setExerciseId}
        metricIds={metricIds}
        setMetricIds={setMetricIds}
        sets={sets}
        setSets={setSets}
        workoutLayers={blockWorkoutLayers}
        printBaseBlock={printBaseBlock}
        setPrintBaseBlock={setPrintBaseBlock}
      />
      <Divider />
      <Box>
        <InputLabel label={t('pageOrientation')} />
        <RadioGroup
          row
          name="orientation"
          defaultValue=""
          value={orientation}
          onChange={(e) => setOrientation(e.target.value as PrintOrientation)}
          sx={{ gap: 1 }}
        >
          <LabeledRadioChip
            key="portrait"
            value="portrait"
            label={t('portrait')}
          />
          <LabeledRadioChip
            key="landscape"
            value="landscape"
            label={t('landscape')}
          />
        </RadioGroup>
      </Box>
      {layout !== 'exercise' && (
        <>
          <Box>
            <InputLabel label={t('printView')} />
            <RadioGroup
              row
              name="view"
              defaultValue=""
              value={view}
              onChange={(e) => setView(e.target.value as PrintView)}
              sx={{ gap: 1 }}
            >
              <LabeledRadioChip
                key="expanded"
                value="expanded"
                label={t('expanded')}
              />
              <LabeledRadioChip
                key="condensed"
                value="condensed"
                label={t('condensed')}
              />
            </RadioGroup>
          </Box>
          <Box>
            <InputLabel label={t('exerciseLettering')} />
            <RadioGroup
              row
              name="lettering"
              defaultValue=""
              value={lettering}
              onChange={(e) => setLettering(e.target.value as PrintLettering)}
              sx={{ gap: 1 }}
            >
              <LabeledRadioChip key="all" value="all" label={t('all')} />
              <LabeledRadioChip
                key="supersets"
                value="supersets"
                label={t('supersetsOnly')}
              />
              <LabeledRadioChip key="none" value="none" label={t('none')} />
            </RadioGroup>
          </Box>
          <Box>
            <InputLabel label={t('eachSidePlacement')} />
            <RadioGroup
              row
              name="eachSidePlacement"
              defaultValue=""
              value={eachSidePlacement}
              onChange={(e) =>
                setEachSidePlacement(e.target.value as PrintEachSidePlacement)
              }
              sx={{ gap: 1 }}
            >
              <LabeledRadioChip
                key="exercise"
                value="exercise"
                label={t('eachSidePlacementExercise')}
              />
              <LabeledRadioChip
                key="set"
                value="set"
                label={t('eachSidePlacementSet')}
              />
            </RadioGroup>
          </Box>
          <Box className="flex">
            <Switch
              size="small"
              checked={fitToPage}
              onChange={(e) => {
                setFitToPage(e.target.checked);
              }}
            />
            <Typography variant="body2" className="ml-2">
              {t('fitToPage')}
            </Typography>
          </Box>
        </>
      )}
      {/* <Box>
              <InputLabel label="Exercise and section instructions" />
              <RadioGroup
                row
                name="instructions"
                value={instructions}
                onChange={(e) =>
                  setInstructions(e.target.value as PrintWorkoutInstructions)
                }
                sx={{ gap: 1 }}
              >
                <LabeledRadioChip
                  key="inline"
                  value="inline"
                  label={t('printInline')}
                />
                {layout !== 'exercise' && (
                  <LabeledRadioChip
                    key="separate"
                    value="separate"
                    label={t('printSeparate')}
                  />
                )}
                <LabeledRadioChip
                  key="none"
                  value="none"
                  label={t('doNotPrint')}
                />
              </RadioGroup>
            </Box> */}
      <>
        <Divider />
        <div className="flex flex-col">
          <TextField
            label={t('headerText')}
            multiline
            maxRows={5}
            minRows={3}
            value={headerText ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setHeaderText(value.length > 0 ? value : null);
              setHeaderTextTooLong(value.length > headerCharLimit);
            }}
          />
          <FormHelperText
            sx={{
              display: 'flex',
              justifyContent: 'right',
              mx: 0,
              mt: 1,
              color: headerTextTooLong ? 'red' : 'inherit',
            }}
          >
            {headerText?.length ?? 0}/{headerCharLimit}
          </FormHelperText>
        </div>
        <Box>
          <InputLabel label={t('headerSize')} />
          <RadioGroup
            row
            name="headerSize"
            defaultValue="normal"
            value={headerSize}
            onChange={(e) => setHeaderSize(e.target.value as PrintHeaderSize)}
            sx={{ gap: 1 }}
          >
            <LabeledRadioChip
              key="normal"
              value="normal"
              label={t('default')}
            />
            <LabeledRadioChip key="large" value="large" label={t('large')} />
          </RadioGroup>
        </Box>
      </>
    </form>
  );
};

const PrintSelect = ({
  layout,
  block,
  blockPrintSettings,
  athletes,
  athleteIds,
  setAthleteIds,
  sessionIds,
  setSessionIds,
  cycleIds,
  setCycleIds,
  exerciseId,
  setExerciseId,
  metricIds,
  setMetricIds,
  sets,
  setSets,
  workoutLayers,
  printBaseBlock,
  setPrintBaseBlock,
}: {
  layout: PrintWorkoutLayout;
  block: BlockPreview;
  blockPrintSettings: BlockPrintSettings;
  athletes: AthletePreview[];
  athleteIds: string[];
  setAthleteIds: (ids: string[]) => void;
  cycleIds: number[];
  setCycleIds: (ids: number[]) => void;
  sessionIds: number[];
  setSessionIds: (ids: number[]) => void;
  exerciseId: string | null;
  setExerciseId: (id: string | null) => void;
  metricIds: string[];
  setMetricIds: (ids: string[]) => void;
  sets: number[] | null;
  setSets: (sets: number[] | null) => void;
  workoutLayers: BlockDraftWorkoutLayer[];
  printBaseBlock: boolean;
  setPrintBaseBlock: (printBaseBlock: boolean) => void;
}) => {
  const { t } = useTranslation();
  const { cycleLabel, sessionLabel } = blockPrintSettings;
  const { setsMetric } = useMetrics();
  const isSingleAthlete = athletes.length === 1;

  // default to athlete when there is only one athlete
  useEffect(() => {
    if (isSingleAthlete && athletes[0]) {
      setAthleteIds([athletes[0].id]);
    }
  }, [isSingleAthlete, athletes, setAthleteIds]);

  const athleteOptions = useMemo(
    () =>
      athletes.map((a) => ({
        id: a.id,
        name: athleteDisplayName(a),
      })),
    [athletes],
  );

  const baseBlockOption = useMemo(
    () => ({
      id: 'baseBlock',
      name: t('baseBlock'),
    }),
    [t],
  );

  const workout = useMemo(
    () =>
      workoutLayers.find(
        (w) =>
          cycleIds.includes(w.cycleSortOrder) &&
          sessionIds.includes(w.sessionSortOrder),
      ),
    [workoutLayers, cycleIds, sessionIds],
  );

  const exerciseOptions = useMemo(() => {
    const layer = workout?.layer;
    if (!layer) return [];
    return layer.exerciseAdds.map((e) => {
      const section = layer.sectionAdds.find(
        (s) => s.workoutSectionId === e.workoutSectionId,
      ) || { name: '' };
      return {
        id: e.workoutExerciseId,
        name: `(${section.name}) ${getExerciseDisplay(e, t)}`,
      };
    });
  }, [workout, t]);

  const metricOptions = useMemo(() => {
    const layer = workout?.layer;
    if (!layer) return [];
    return layer.exerciseMetricAdds
      .filter(
        (m) => m.workoutExerciseId === exerciseId && m.metricType !== 'sets',
      )
      .map((m) => ({ id: m.metricId, name: m.metricName }));
  }, [workout, exerciseId]);

  const setOptions = useMemo(() => {
    if (!workout?.layer || !exerciseId) return [];

    const { layer } = workout;
    const setAdds = layer.setAdds.filter(
      (s) => s.workoutExerciseId === exerciseId,
    );
    const setIds = sortByOrder(setAdds).map((s) => s.workoutSetId);
    const setMetrics = layer.setMetrics.filter(
      (m) => m.metricId === setsMetric.id && setIds.includes(m.workoutSetId),
    );

    // Use the "Sets" metric to determine how many sets there actually are
    let totalSets = 0;
    return setMetrics.flatMap((m) => {
      const setCount = parseInt(m.input ?? '1');
      return Array.from({ length: setCount }, (_) => ({
        name: t('setNum', { num: totalSets + 1 }),
        id: String(totalSets++),
      }));
    });
  }, [workout, exerciseId, t, setsMetric.id]);

  const athleteSelectionOptions = useMemo(() => {
    return [
      ...(layout !== 'exercise' && !isSingleAthlete ? [baseBlockOption] : []),
      ...athleteOptions,
    ];
  }, [baseBlockOption, athleteOptions, layout, isSingleAthlete]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <WorkoutSelector
        layout={layout}
        block={block}
        cycleLabel={cycleLabel}
        cycleIds={cycleIds}
        setCycleIds={setCycleIds}
        sessionLabel={sessionLabel}
        sessionIds={sessionIds}
        setSessionIds={setSessionIds}
        setExerciseId={setExerciseId}
        setSets={setSets}
      />
      {layout === 'exercise' && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <InputLabel label={t('exercise')} />
            <Select
              value={exerciseId || ''}
              disabled={!cycleIds.length || !sessionIds.length}
              onChange={(e) => {
                setExerciseId(e.target.value === '' ? null : e.target.value);
                setSets(null);
              }}
              displayEmpty
              renderValue={(selected) => {
                // We have to manually render the placeholder on a select
                return selected.length ? (
                  exerciseOptions.find((o) => o.id === selected)?.name || ''
                ) : (
                  <div className="text-gray-600">{t('selectAnExercise')}</div>
                );
              }}
            >
              {exerciseOptions.map((e) => (
                <MenuItem value={e.id} key={e.id}>
                  {e.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <MultiSelect
              label={t('metric')}
              disabled={!cycleIds.length || !sessionIds.length || !exerciseId}
              placeholder={t('selectAMetric')}
              options={metricOptions}
              selectedOptions={metricOptions.filter((m) =>
                metricIds.includes(m.id),
              )}
              onChange={(e) => setMetricIds(e.map((m) => m.id))}
              optionToName={(m) => m.name}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <MultiSelect
              label={t('sets')}
              disabled={!exerciseId}
              placeholder={t('allSets')}
              options={setOptions}
              selectedOptions={setOptions.filter(
                (s) => sets?.includes(Number(s.id)),
              )}
              onChange={(e) => setSets(e.map((s) => Number(s.id)))}
              optionToName={(s) => s.name}
            />
          </Box>
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 0.5,
        }}
      >
        <MultiSelect
          disabled={isSingleAthlete}
          label={t('athleteSelection')}
          placeholder={t('selectAthletes')}
          options={athleteSelectionOptions}
          selectedOptions={[
            ...(printBaseBlock ? [baseBlockOption] : []),
            ...athleteOptions.filter((a) => athleteIds.includes(a.id)),
          ]}
          onChange={(e) => {
            const hasBaseBlock = e.some((a) => a.id === 'baseBlock');
            setPrintBaseBlock(hasBaseBlock);
            const athleteIds = e
              .filter((a) => a.id !== 'baseBlock')
              .map((a) => a.id);
            setAthleteIds(athleteIds);
          }}
          optionToName={(a) => a.name}
          showSelectAll={true}
        />
      </Box>
    </Box>
  );
};

// exported for testing
export const isValidFiltersForLayout = (
  layout: PrintWorkoutLayout,
  sessionIds: number[],
  cycleIds: number[],
  exerciseId: string | null,
  metricIds: string[],
) => {
  if (layout === 'workout') {
    return !!sessionIds.length && !!cycleIds.length;
  } else if (layout === 'cycle') {
    return !!cycleIds.length;
  } else if (layout === 'session' || layout === 'uniform-session') {
    return !!sessionIds.length;
  } else if (layout === 'exercise') {
    return (
      !!sessionIds.length &&
      !!cycleIds.length &&
      !!exerciseId &&
      !!metricIds.length
    );
  }

  throw new Error('Invalid layout');
};

type CompareResult = {
  athleteId: string | null;
  results: ReturnType<typeof compareWorkouts>;
};

function validateBlockUniformity(
  block: BlockPreview,
  athleteIds: string[],
  blockWorkoutLayers: BlockDraftWorkoutLayer[],
  athleteWorkoutLayers: AthleteDraftWorkoutLayer[],
  t: I18nTranslator,
  printBaseBlock: boolean,
): CompareResult[] {
  if (blockWorkoutLayers.length === 0) return [];

  const checkUniformity = (athleteId?: string) => {
    const workouts: ComparableWorkout[] = blockWorkoutLayers.map(
      (blockLayer) => {
        const athleteLayer = athleteId
          ? athleteWorkoutLayers.find(
              (l) =>
                l.profileId === athleteId &&
                l.workoutId === blockLayer.workoutId,
            )
          : undefined;

        return {
          ...mergeWorkoutLayers(blockLayer, athleteLayer),
          cycleSortOrder: blockLayer.cycleSortOrder,
          sessionSortOrder: blockLayer.sessionSortOrder,
        };
      },
    );

    return {
      athleteId: athleteId ?? null,
      results: compareWorkouts(t, block, ...workouts),
    };
  };

  const toCheck = [...(printBaseBlock ? [undefined] : []), ...athleteIds];

  return toCheck.reduce((results, athleteId) => {
    const result = checkUniformity(athleteId);
    if (Object.values(result.results).some((value) => value.length > 0)) {
      results.push(result);
    }
    return results;
  }, [] as CompareResult[]);
}

export type PrintedExerciseTarget = PrintedExerciseTargetBase & {
  workoutId: string;
};

/**
 * Resolve the picked exercise to the identity the print worker matches on. The
 * exercise dropdown reads the block layer, while every athlete with their own
 * draft layer holds a copy of the workout with different workoutExerciseIds for
 * the same exercise, so library id, section name, and occurrence identify the
 * exercise across layers.
 */
export function resolvePrintedExercise(
  blockLayer: BlockDraftWorkoutLayer | undefined,
  exerciseId: string | null,
): PrintedExerciseTarget | null {
  if (!blockLayer || !exerciseId) return null;

  const target = resolvePrintedExerciseTarget(blockLayer.layer, exerciseId);
  if (!target) return null;

  return { ...target, workoutId: blockLayer.workoutId };
}

/**
 * Athletes whose resolved workout has no exercise with the target's library id
 * in the target's section. Their rows are absent from the printed card.
 */
export function findAthletesMissingExercise(
  athleteIds: string[],
  blockLayer: BlockDraftWorkoutLayer,
  athleteLayers: AthleteDraftWorkoutLayer[],
  target: PrintedExerciseTarget,
): string[] {
  return athleteIds.filter((athleteId) => {
    const athleteLayer = athleteLayers.find(
      (l) => l.profileId === athleteId && l.workoutId === target.workoutId,
    );
    const workout = mergeWorkoutLayers(blockLayer, athleteLayer);

    return !findPrintedExercise(workout, target);
  });
}

type HasWorkoutId = { workoutId: string };

// exported for testing
export function resolvePrintJobWorkouts(
  selectedBlockLayers: HasWorkoutId[],
  selectedAthleteLayers: HasWorkoutId[],
): string[] {
  return Array.from(
    new Set([
      ...selectedBlockLayers.map((w) => w.workoutId),
      ...selectedAthleteLayers.map((w) => w.workoutId),
    ]),
  );
}

type HasCycleAndSession = { cycleSortOrder: number; sessionSortOrder: number };
const getWorkoutsForLayoutAndFilters = (
  layout: PrintWorkoutLayout,
  blockWorkoutLayers: BlockDraftWorkoutLayer[],
  athleteWorkoutLayers: AthleteDraftWorkoutLayer[],
  sessionIds: number[],
  cycleIds: number[],
) => {
  const cycleFilter = (w: HasCycleAndSession) =>
    cycleIds.includes(w.cycleSortOrder);
  const sessionFilter = (w: HasCycleAndSession) =>
    sessionIds.includes(w.sessionSortOrder);

  // If sessionIds is empty, we don't need to filter by session
  // If cycleIds is empty, we don't need to filter by cycle
  // If neither are empty, we need to filter by both
  const filter =
    sessionIds.length === 0
      ? cycleFilter
      : cycleIds.length === 0
        ? sessionFilter
        : (w: HasCycleAndSession) => cycleFilter(w) && sessionFilter(w);

  return {
    selectedBlockLayers: blockWorkoutLayers.filter(filter),
    selectedAthleteLayers: athleteWorkoutLayers.filter(filter),
  };
};
